import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { WS_BASE_URL } from '../config';

interface SocketContextType {
    socket: Socket | null;
    connected: boolean;
}

const SocketContext = createContext<SocketContextType>({ socket: null, connected: false });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [connected, setConnected] = useState(false);

    const { user } = useAuth();

    useEffect(() => {
        if (!user) {
            return;
        }

        // Determine URL based on environment or window location
        const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
        const getSocketUrl = (url: string) => {
            const currentPort = window.location.port;
            // Dynamic regional port resolution based on active host port (ADR-110 & FIX-008)
            if (currentPort === '3003' || currentPort === '3001') {
                return 'http://localhost:4001';
            }
            if (currentPort === '3002' || currentPort === '3000') {
                return 'http://localhost:4000';
            }

            try {
                if (!url || url.startsWith('/')) {
                    return window.location.origin;
                }
                return new URL(url).origin;
            } catch (e) {
                return window.location.origin;
            }
        };
        const SOCKET_URL = getSocketUrl(apiUrl);

        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        const cleanToken = token ? (token.startsWith('Bearer ') ? token.substring(7) : token) : undefined;

        const newSocket = io(SOCKET_URL, {
            auth: cleanToken ? { token: cleanToken } : undefined,
            transports: ['websocket', 'polling'], // Try websocket first
            reconnectionAttempts: 5,
        });

        newSocket.on('connect', () => {
            setConnected(true);
        });

        newSocket.on('disconnect', () => {
            setConnected(false);
        });

        newSocket.on('connect_error', (err) => {
            console.error('⚠️ WebSocket Connection Error:', err);
        });

        setSocket(newSocket);

        return () => {
            newSocket.close();
        };
    }, [user]);

    return (
        <SocketContext.Provider value={{ socket, connected }}>
            {children}
        </SocketContext.Provider>
    );
};
